import {
  AllHTMLAttributes,
  MutableRefObject,
  RefCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {
  arrow,
  autoUpdate,
  FloatingContext,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  type UseInteractionsReturn,
} from '@floating-ui/react';

type UserOverrides = AllHTMLAttributes<HTMLElement>;
type UseToggletip = {
  getTargetProps: (o?: UserOverrides) => ReturnType<
    UseInteractionsReturn['getReferenceProps']
  > & {
    ref: RefCallback<HTMLElement>;
    'aria-haspopup': 'dialog';
    'aria-expanded': boolean;
    'aria-controls': string | undefined;
  };
  getToggletipProps: (o?: UserOverrides) => ReturnType<
    UseInteractionsReturn['getFloatingProps']
  > & {
    role: 'dialog';
    id: string;
    ref: RefCallback<HTMLElement>;
    arrowRef: MutableRefObject<null>;
    context: FloatingContext;
    handleClose: () => void;
  };
};
// this is essentially an interactive tooltip, a tooltip that opens on click and has a handle close

type UseToggletipOptions = {
  placement: Placement;
  isOpenState: boolean;
  setIsOpenState: (isOpen: boolean) => void;
  localStorageKey?: string;
};

export const useToggletip = ({
  placement,
  isOpenState,
  setIsOpenState,
  localStorageKey,
}: UseToggletipOptions): UseToggletip => {
  const [isTooltipClosed, setIsTooltipClosed] = useState(false);
  const arrowRef = useRef(null);
  const toggletipId = useId();

  // UseEffect to handle localStorage for each tooltip individually
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const closedState =
        localStorageKey && localStorage.getItem(localStorageKey);
      setIsTooltipClosed(closedState === 'true');
    } else {
      setIsTooltipClosed(false); // Fallback for environments without localStorage
    }
  }, [localStorageKey]);

  const handleClose = () => {
    setIsOpenState(false);
    // Set the localStorage value to prevent showing the tooltip again
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorageKey && localStorage.setItem(localStorageKey, 'true');
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpenState && !isTooltipClosed,
    onOpenChange: setIsOpenState,
    placement,
    strategy: 'absolute',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  return {
    getTargetProps: (userOverrides: AllHTMLAttributes<HTMLElement> = {}) => {
      return {
        ...getReferenceProps({
          ...userOverrides,
          onClick: () => setIsOpenState(!isOpenState),
        }),
        ref: refs.setReference,
        'aria-haspopup': 'dialog' as const,
        'aria-expanded': isOpenState,
        'aria-controls': isOpenState ? toggletipId : undefined,
      };
    },
    getToggletipProps: (userOverrides: AllHTMLAttributes<HTMLElement> = {}) => {
      return {
        ...getFloatingProps({
          ...userOverrides,
          style: {
            ...floatingStyles,
            ...userOverrides.style,
          },
        }),
        role: 'dialog',
        id: toggletipId,
        ref: refs.setFloating,
        arrowRef,
        context,
        handleClose,
      };
    },
  };
};
